import { cn } from '@/lib/utils';
import { useLocalization } from '@/hooks/useLocalization';
import type { Tables } from '@/integrations/supabase/types';
import { ProjectFactsSection } from './ProjectFactsSection';

type Row = Tables<'project_dependencies'>;

const STATUS_STYLE: Record<string, string> = {
  'on track': 'bg-emerald-500/15 text-emerald-600',
  'at risk': 'bg-amber-500/15 text-amber-600',
  late: 'bg-destructive/15 text-destructive',
  blocked: 'bg-destructive/15 text-destructive',
};

const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null);
const dirOf = (v: string | null) => (v && /^in/i.test(v) ? 'inbound' : v && /^out/i.test(v) ? 'outbound' : null);

function DependencyLine({ description, direction, owner, neededOn, status }: {
  description: string; direction: string | null; owner: string | null; neededOn: string | null; status: string | null;
}) {
  const { t, tx } = useLocalization();
  const dir = dirOf(direction);
  const meta = [
    dir === 'inbound' ? t('Waiting on') : dir === 'outbound' ? t('Others depend on this') : null,
    owner,
    neededOn ? `${t('Needed by')} ${neededOn}` : null,
  ].filter(Boolean).join(' · ');
  return (
    <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
      <div className="min-w-0">
        <p className="text-sm font-medium leading-snug">{tx(description)}</p>
        {meta && <p className="mt-0.5 text-xs text-muted-foreground">{meta}</p>}
      </div>
      {status && (
        <span className={cn('inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium', STATUS_STYLE[status] ?? 'bg-muted text-muted-foreground')}>
          {tx(status)}
        </span>
      )}
    </div>
  );
}

/** Dependencies read from each project's RAID log, reviewed per project. */
export function ProjectDependenciesSection() {
  return (
    <ProjectFactsSection<Row>
      kind="dependency"
      title="Project dependencies"
      description="From each project's RAID log: what a project is waiting on and what others need from it. Late ones raise a signal once added."
      noun="dependencies"
      searchText={(r) => ('description' in r ? r.description : str((r as { payload: Record<string, unknown> }).payload.description) ?? '') + ' ' + ('owner' in r ? r.owner ?? '' : '')}
      renderDetected={(f) => (
        <DependencyLine
          description={str(f.payload.description) ?? f.item_key} direction={str(f.payload.direction)} owner={str(f.payload.owner)}
          neededOn={str(f.payload.neededBy)} status={str(f.payload.status)?.toLowerCase() ?? null}
        />
      )}
      renderApproved={(r) => (
        <DependencyLine description={r.description} direction={r.direction} owner={r.owner} neededOn={r.needed_on} status={r.status} />
      )}
    />
  );
}

export default ProjectDependenciesSection;
